import { useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";

import BaseDialog from "@/components/Modals/BaseDialog";
import { NoxusInputField } from "@/components/Form/InputField";
import { SpotToast } from "@/components/SpotToast";
import { TablesService, TableWithCount } from "@/generated";
import { DATA_KEYS, useRemoteData } from "@/utils/useRemoteData";
import { useSpotUser } from "@/utils/spotUser";
import { useDialogMutationLoading } from "@/hooks/useDialogMutationLoading";
import {
  MAX_ENTITY_DESCRIPTION,
  MAX_ENTITY_NAME,
} from "@/constants/entityLimits";

type FormData = {
  name: string;
  description: string;
};

export function EditTableDialog({
  table,
  isOpen,
  onOpenChange,
}: {
  table: TableWithCount | null;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { spotGroup } = useSpotUser();
  const queryClient = useQueryClient();

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isValid },
  } = useForm<FormData>({
    mode: "onChange",
    defaultValues: { name: "", description: "" },
  });

  useEffect(() => {
    if (table)
      reset({ name: table.name, description: table.description ?? "" });
  }, [table, reset]);

  const nameValue = watch("name");
  const { data: existingTables } = useRemoteData(
    DATA_KEYS.TABLES,
    { path: { group_id: spotGroup.id } },
    { enabled: isOpen }
  );
  const nameTaken =
    nameValue.trim().length > 0 &&
    (existingTables ?? []).some(
      (t) =>
        t.id !== table?.id &&
        t.name.trim().toLowerCase() === nameValue.trim().toLowerCase()
    );

  const updateMutation = useMutation({
    mutationFn: TablesService.updateTable,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [DATA_KEYS.TABLES] });
      queryClient.invalidateQueries({ queryKey: [DATA_KEYS.TABLES_ID] });
      onOpenChange(false);
    },
    onError: () => SpotToast.error("Failed to update table"),
  });

  const isSaving = useDialogMutationLoading(isOpen, updateMutation);

  const onSubmit = (data: FormData) => {
    if (!table || nameTaken) return;
    updateMutation.mutate({
      path: { group_id: spotGroup.id, table_id: table.id },
      body: { name: data.name.trim(), description: data.description.trim() },
    });
  };

  return (
    <BaseDialog
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title="Edit table"
      description="Rename this table or update its description."
      footerActions={[
        {
          label: "Cancel",
          props: {
            variant: "outline",
            onClick: () => onOpenChange(false),
            disabled: isSaving,
          },
        },
        {
          label: "Save",
          props: {
            onClick: handleSubmit(onSubmit),
            type: "submit",
            disabled: !isValid || nameTaken,
            loading: isSaving,
          },
        },
      ]}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="space-y-4">
          <NoxusInputField
            name="name"
            label="Name"
            control={control}
            errors={errors}
            placeholder="e.g. Customers"
            autoFocus
            maxLength={MAX_ENTITY_NAME}
            helperText={
              nameTaken ? (
                <span className="text-destructive">
                  A table with this name already exists.
                </span>
              ) : undefined
            }
            rules={{
              required: { value: true, message: "Table name is required." },
              validate: (value: string) =>
                value.trim().length > 0 || "Table name is required.",
            }}
          />
          <NoxusInputField
            name="description"
            label="Description"
            control={control}
            errors={errors}
            placeholder="What does this table hold?"
            isTextarea
            customStyle="h-24"
            maxLength={MAX_ENTITY_DESCRIPTION}
          />
        </div>
      </form>
    </BaseDialog>
  );
}
